import axios from "axios";
import {
  API_BASE_URL,
  DEFAULT_HEADERS,
  REQUEST_TIMEOUT,
  STORAGE_KEYS,
  ERROR_MESSAGES,
} from "./config";

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: DEFAULT_HEADERS,
});

// Centralized upload timeout (2 minutes)
export const UPLOAD_TIMEOUT = 120000;

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Handle FormData requests - remove Content-Type header to let browser set it with boundary
    // More robust FormData detection
    const isFormData = config.data instanceof FormData ||
      config.data?.constructor?.name === 'FormData' ||
      (config.data && typeof config.data === 'object' && config.data.entries);

    if (isFormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle responses and errors
apiClient.interceptors.response.use(
  (response) => {
    // Handle blob responses (for file downloads)
    if (response.data instanceof Blob || (response.data && typeof response.data === 'object' && response.data.constructor && response.data.constructor.name === 'Blob')) {
      return response.data;
    }

    // Handle case where response.data might be null but response has the data
    if (response.data === null && response.status === 200) {
      // For DELETE requests, sometimes the data is in the response itself
      const responseData = {
        responseCode: response.status,
        responseMessage: "Operation completed successfully",
        data: null,
      };
      return responseData;
    }

    // Return the response data directly
    return response.data;
  },
  async (error) => {
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      // Prefer backend-provided message (supports both `message` and `responseMessage`)
      const getBackendMessage = (fallbackKey) => {
        if (!data || typeof data !== 'object') return ERROR_MESSAGES[fallbackKey] || ERROR_MESSAGES.SERVER_ERROR;
        return (
          data.message ||
          data.responseMessage ||
          data.error ||
          ERROR_MESSAGES[fallbackKey] ||
          ERROR_MESSAGES.SERVER_ERROR
        );
      };

      // For blob responses, we need to parse the error differently
      if (data instanceof Blob) {
        try {
          const errorText = await data.text();
          const errorData = JSON.parse(errorText);

          // Create a new error with parsed data
          const parsedError = new Error(
            errorData.responseMessage || ERROR_MESSAGES.SERVER_ERROR
          );
          parsedError.response = {
            status: status,
            data: errorData,
          };
          throw parsedError;
        } catch (parseError) {
          throw new Error(parseError.message || ERROR_MESSAGES.SERVER_ERROR);
        }
      }

      switch (status) {
        case 400:
          // Bad request - validation errors, etc.
          // Preserve errors object for detailed validation messages
          const validationError = new Error(getBackendMessage("BAD_REQUEST"));
          // Attach the full response data to the error object for detailed error handling
          validationError.responseData = data;
          if (data && data.errors) {
            validationError.errors = data.errors;
          }
          // Also preserve the original response for compatibility
          validationError.response = error.response;
          throw validationError;
        case 401:
          // Unauthorized - Token expired or invalid
          // Always clear auth data and redirect to login (unless already on login page)

          // Clear all auth data
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);

          // Only redirect if not already on login page (prevents reload loop on failed login)
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && !currentPath.includes('/login')) {
            // Redirect to login page from anywhere in the app
            // Use window.location.replace to prevent back button navigation
            window.location.replace('/login');
          } else {
          }

          // Throw error to stop further processing
          throw new Error(getBackendMessage("UNAUTHORIZED") || "Session expired. Please login again.");
          break;
        case 403:
          // Forbidden
          throw new Error(getBackendMessage("FORBIDDEN"));
        case 404:
          // Not found
          throw new Error(getBackendMessage("NOT_FOUND"));
        case 409:
          // Conflict - duplicate resource
          const conflictError = new Error(getBackendMessage("BAD_REQUEST")); // Use getBackendMessage for extraction
          conflictError.responseData = data;
          conflictError.response = error.response;
          throw conflictError;
        case 422:
          // Validation error
          throw new Error(getBackendMessage("VALIDATION_ERROR"));
        case 500:
          // Server error
          throw new Error(getBackendMessage("SERVER_ERROR"));
        default:
          // Other server errors
          throw new Error(getBackendMessage("SERVER_ERROR"));
      }
    } else if (error.request) {
      // Network error
      throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
    } else {
      // Other errors
      throw new Error(error.message || ERROR_MESSAGES.UNKNOWN_ERROR);
    }
  }
);

// HTTP methods
export const get = (url, config = {}) => apiClient.get(url, config);
export const post = (url, data = {}, config = {}) =>
  apiClient.post(url, data, config);
export const put = (url, data = {}, config = {}) =>
  apiClient.put(url, data, config);
export const patch = (url, data = {}, config = {}) =>
  apiClient.patch(url, data, config);
export const del = (url, config = {}) => apiClient.delete(url, config);

// Utility functions
export const isAuthenticated = () => {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  return !!token;
};

export const getAuthToken = () => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

export const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
};

export const setAuthData = (
  token,
  userData,
  rememberMe = false
) => {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  if (userData) {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  }
  if (rememberMe) {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, "true");
  }
};

export default apiClient;
